import {Modal} from '@mantine/core';
import type {
  ModalBaseCloseButtonProps,
  ModalBaseOverlayProps,
} from '@mantine/core';
import type {FC, ReactNode} from 'react';
import {modalConfig} from './modalConfig';

type Props = {
  opened: boolean;
  onClose: () => void;
  // 「あとで」「×」の data-gtm-click に使う識別子
  componentId: string;
  // 見出し要素の id。Mantine の Modal は title を使わないとダイアログに名前が付かないため
  labelledBy?: string;
  // 「あとで」の代わりに独自の操作を置くポップアップで外す
  withLaterButton?: boolean;
  // 背景クリックで閉じる操作も「×」と同じ名前で計測したいポップアップだけ有効にする
  withOverlayGtmClick?: boolean;
  children: ReactNode;
};

/**
 * ユーザーの操作なく出る訴求ポップアップ共通の枠。
 */
export const PromptModal: FC<Props> = ({
  opened,
  onClose,
  componentId,
  labelledBy,
  withLaterButton = true,
  withOverlayGtmClick = false,
  children,
}) => {
  // ModalBaseCloseButtonProps は button の props を継承しており data 属性は実際には通るが、
  // オブジェクトリテラルの超過プロパティチェックに弾かれるためアサーションで抑える
  const closeButtonProps = {
    'aria-label': '閉じる',
    'data-gtm-click': `close-${componentId}`,
  } as ModalBaseCloseButtonProps;
  // closeButtonProps と同じ理由でアサーションする
  const overlayProps = withOverlayGtmClick
    ? ({'data-gtm-click': `close-${componentId}`} as ModalBaseOverlayProps)
    : undefined;

  return (
    <Modal.Root
      className="w-[343px] pc:w-[560px]"
      radius={16}
      opened={opened}
      onClose={onClose}
      centered={modalConfig.centered}
      padding={modalConfig.padding}
      transitionProps={{duration: 200, transition: 'pop'}}
      styles={{
        // タイトルを置かないため、既定の space-between だとバツが左に寄る
        header: {padding: 12, paddingBottom: 0, justifyContent: 'flex-end'},
      }}
    >
      <Modal.Overlay {...overlayProps} />
      <Modal.Content aria-labelledby={labelledBy}>
        {/* ユーザーの操作なく出るモーダルなので、本文をスクロールせず閉じられるようにする。
            Mantine のヘッダーは sticky なので、中身をスクロールしてもバツは残る */}
        <Modal.Header>
          <Modal.CloseButton {...closeButtonProps} />
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-y-12 rounded-16 bg-white p-20 pc:gap-y-16 pc:p-24">
            {children}

            {withLaterButton && (
              <button
                type="button"
                onClick={onClose}
                className="text-base text-black-3 font-bold mx-auto hover:opacity-70"
                data-gtm-click={`later-${componentId}`}
              >
                あとで
              </button>
            )}
          </div>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
